import axios from 'axios';
import { API_BASE_URL, PREMIUM_CDN_BASE_URL } from '../config/api';

/**
 * In-memory cache for signed URLs.
 * Key: original unsigned CDN path, Value: { signedUrl, expiresAt (ms) }
 */
const cache = new Map();

/** Buffer (in ms) before we consider a cached URL expired. */
const EXPIRY_BUFFER_MS = 60_000; // refresh 1 min before actual expiry

/**
 * Extract the CDN path from a full URL that uses PREMIUM_CDN_BASE_URL.
 * e.g. "https://fideprep-premium.b-cdn.net/topic_audio/..." → "/topic_audio/..."
 */
function extractPath(fullUrl) {
  try {
    const urlObj = new URL(fullUrl);
    return urlObj.pathname;
  } catch (e) {
    // Fallback if not a full valid URL, just normalized path
    return '/' + fullUrl.replace(/^\/+/, '');
  }
}

/**
 * Given an array of full CDN URLs (using PREMIUM_CDN_BASE_URL prefix),
 * return a Map of original URL → signed URL.
 *
 * Cached signed URLs are reused until they are close to expiry.
 *
 * @param {string[]} urls - Full CDN URLs to sign
 * @param {string} userToken - Firebase auth token
 * @returns {Promise<Map<string, string>>} originalUrl → signedUrl
 */
export async function signCdnUrls(urls, userToken) {
  const result = new Map();
  const toSign = [];

  const now = Date.now();

  for (const url of urls) {
    const path = extractPath(url);
    const cached = cache.get(path);
    if (cached && cached.expiresAt > now + EXPIRY_BUFFER_MS) {
      result.set(url, cached.signedUrl);
    } else {
      toSign.push({ url, path });
    }
  }

  if (toSign.length === 0) return result;

  // Call backend batch signing endpoint
  const paths = toSign.map((t) => t.path);

  try {
    const headers = {};
    if (userToken) {
      headers.Authorization = `Bearer ${userToken}`;
    }

    const res = await axios.post(`${API_BASE_URL}/api/cdn/sign`, { paths }, { headers });

    const signed = res.data.urls; // [{ path, signedUrl, expires }]
    const signedByPath = new Map();
    for (const item of signed) {
      signedByPath.set(item.path, item);
    }

    for (const { url, path } of toSign) {
      const entry = signedByPath.get(path);
      if (entry) {
        cache.set(path, {
          signedUrl: entry.signedUrl,
          expiresAt: entry.expires * 1000, // convert unix seconds → ms
        });
        result.set(url, entry.signedUrl);
      }
      // If not in response (denied), leave out of result
    }
  } catch (err) {
    console.error('[cdnAuth] Failed to sign CDN URLs:', err);
    // Fall through — caller should handle missing entries
  }

  return result;
}

/**
 * Sign a single CDN URL. Convenience wrapper.
 * @param {string} url - Full CDN URL
 * @param {string} userToken - Firebase auth token
 * @returns {Promise<string|null>} Signed URL or null if signing failed/denied
 */
export async function signCdnUrl(url, userToken) {
  const map = await signCdnUrls([url], userToken);
  return map.get(url) || null;
}

/**
 * Clear the signed URL cache. Useful on logout.
 */
export function clearCdnCache() {
  cache.clear();
}
